import { useEffect, useMemo, useState } from "react";
import { Bot, Eye, EyeOff, Megaphone, Plus, RefreshCw, Save, Sparkles, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const emptyForm = { twitch_login: "", display_name: "", enabled: true, source: "manual", notes: "", cached_ai_shoutout: "" };
const inputClass = "w-full h-10 px-3 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";
const textareaClass = "w-full min-h-[92px] px-3 py-2 rounded bg-[hsl(var(--surface-2))] border border-[hsl(var(--border))] text-sm outline-none focus:border-[hsl(var(--primary))]";

export default function AutoShoutouts() {
  const [targets, setTargets] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [draft, setDraft] = useState(emptyForm);
  const [popupPresets, setPopupPresets] = useState([]);
  const [popupSettings, setPopupSettings] = useState({ popup_preset_id: "" });
  const [filters, setFilters] = useState({ q: "", enabled: "", source: "" });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const selected = useMemo(() => targets.find((item) => item.id === selectedId) || targets[0] || null, [targets, selectedId]);
  const selectedPopupPreset = useMemo(
    () => popupPresets.find((preset) => preset.id === popupSettings.popup_preset_id) || null,
    [popupPresets, popupSettings.popup_preset_id]
  );

  const load = async () => {
    setLoading(true);
    try {
      const [targetsResponse, popupResponse, settingsResponse] = await Promise.all([
        api.get("/admin/auto-shoutouts", { params: filters }),
        api.get("/admin/popup-presets"),
        api.get("/admin/auto-shoutouts/settings"),
      ]);
      const next = targetsResponse.data.targets || [];
      setTargets(next);
      setSelectedId((current) => next.find((item) => item.id === current)?.id || next[0]?.id || "");
      setPopupPresets(popupResponse.data?.presets || []);
      setPopupSettings(settingsResponse.data?.popup || { popup_preset_id: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load auto shoutouts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!selected) {
      setDraft(emptyForm);
      return;
    }
    setDraft({
      twitch_login: selected.twitch_login || "",
      display_name: selected.display_name || "",
      enabled: selected.enabled !== false,
      source: selected.source || "manual",
      notes: selected.notes || "",
      cached_ai_shoutout: selected.cached_ai_shoutout || "",
    });
  }, [selected]);

  const run = async (label, action) => {
    setBusy(label);
    try {
      const changed = await action();
      if (changed) {
        setTargets((current) => {
          const exists = current.some((item) => item.id === changed.id);
          return exists ? current.map((item) => (item.id === changed.id ? changed : item)) : [changed, ...current];
        });
        setSelectedId(changed.id);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Auto shoutout action failed");
    } finally {
      setBusy("");
    }
  };

  const createTarget = async (event) => {
    event.preventDefault();
    await run("create", async () => {
      const { data } = await api.post("/admin/auto-shoutouts", form);
      setForm(emptyForm);
      toast.success("Auto shoutout added");
      return data;
    });
  };

  const saveTarget = async () => {
    if (!selected) return;
    await run("save", async () => {
      const { data } = await api.patch(`/admin/auto-shoutouts/${selected.id}`, draft);
      toast.success("Auto shoutout saved");
      return data;
    });
  };

  const deleteTarget = async () => {
    if (!selected || !window.confirm(`Delete auto shoutout for @${selected.twitch_login}?`)) return;
    setBusy("delete");
    try {
      await api.delete(`/admin/auto-shoutouts/${selected.id}`);
      toast.success("Auto shoutout deleted");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not delete auto shoutout");
    } finally {
      setBusy("");
    }
  };

  const targetAction = async (action, label) => {
    if (!selected) return;
    await run(action, async () => {
      const { data } = await api.post(`/admin/auto-shoutouts/${selected.id}/${action}`);
      toast.success(label);
      return data.target || data;
    });
  };

  const savePopupSettings = async (nextSettings = popupSettings) => {
    setBusy("popup-settings");
    try {
      const { data } = await api.patch("/admin/auto-shoutouts/settings", nextSettings);
      setPopupSettings(data?.popup || { popup_preset_id: "" });
      toast.success("Shoutout popup settings saved");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save shoutout popup settings");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="space-y-6" data-testid="auto-shoutouts-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="label-eyebrow">Twitch creators</div>
          <h1 className="text-2xl font-semibold tracking-tight mt-1 flex items-center gap-2"><Megaphone className="w-6 h-6" /> Auto Shoutouts</h1>
        </div>
        <button onClick={load} className="text-xs uppercase tracking-widest border border-[hsl(var(--border))] rounded px-3 py-2 flex items-center gap-2">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="surface p-5">
        <form onSubmit={createTarget} className="grid grid-cols-1 lg:grid-cols-6 gap-3 items-end">
          <Field label="Login"><input className={inputClass} value={form.twitch_login} onChange={(e) => setForm({ ...form, twitch_login: e.target.value })} placeholder="creatorlogin" required /></Field>
          <Field label="Display name"><input className={inputClass} value={form.display_name} onChange={(e) => setForm({ ...form, display_name: e.target.value })} /></Field>
          <Field label="Source">
            <select className={inputClass} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              <option value="manual">manual</option>
              <option value="raid">raid</option>
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm h-10"><input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} /> Enabled</label>
          <div className="lg:col-span-2">
            <button type="submit" disabled={busy === "create"} className="w-full bg-[hsl(var(--primary))] text-white rounded h-10 flex items-center justify-center gap-2 text-sm"><Plus className="w-4 h-4" /> Add Creator</button>
          </div>
        </form>
      </div>

      <div className="surface p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <Field label="Search"><input className={inputClass} value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} /></Field>
          <Field label="Enabled">
            <select className={inputClass} value={filters.enabled} onChange={(e) => setFilters({ ...filters, enabled: e.target.value })}>
              <option value="">Any</option>
              <option value="true">Enabled</option>
              <option value="false">Disabled</option>
            </select>
          </Field>
          <Field label="Source">
            <select className={inputClass} value={filters.source} onChange={(e) => setFilters({ ...filters, source: e.target.value })}>
              <option value="">Any</option>
              <option value="manual">Manual</option>
              <option value="raid">Raid</option>
            </select>
          </Field>
          <button onClick={load} className="self-end h-10 border border-[hsl(var(--border))] rounded text-sm">Apply filters</button>
        </div>
      </div>

      <div className="surface p-5">
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex-1">
            <div className="label-eyebrow">Popup style</div>
            <h2 className="text-lg font-semibold mt-1">Shoutout popup</h2>
            <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
              Choose a popup preset for shoutouts. Color, placement, icon, duration, and layout are edited in <a href="/app/popups" className="text-[hsl(var(--primary))] hover:underline">Popup Editor</a>.
            </p>
          </div>
          <Field label="Popup preset">
            <select
              className={inputClass}
              value={popupSettings.popup_preset_id || ""}
              onChange={(e) => {
                const next = { ...popupSettings, popup_preset_id: e.target.value };
                setPopupSettings(next);
                savePopupSettings(next);
              }}
            >
              <option value="">Built-in Shoutout style</option>
              {popupPresets.map((preset) => (
                <option key={preset.id} value={preset.id}>{preset.name}</option>
              ))}
            </select>
          </Field>
          <button
            onClick={() => savePopupSettings()}
            disabled={busy === "popup-settings"}
            className="h-10 px-4 rounded bg-[hsl(var(--primary))] text-white text-sm flex items-center justify-center gap-2 disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> Save Popup
          </button>
        </div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-[hsl(var(--muted-foreground))]">
          <Info label="Label" value="Shoutout" />
          <Info label="Color" value={selectedPopupPreset?.accent_color || "Built-in purple"} />
          <Info label="Placement" value={selectedPopupPreset?.placement?.anchor || "top-right"} />
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[360px_1fr] gap-4">
        <div className="surface overflow-hidden">
          {loading ? <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">Loading...</div> : null}
          {!loading && targets.length === 0 ? <div className="p-5 text-sm text-[hsl(var(--muted-foreground))]">No auto shoutouts yet.</div> : null}
          {targets.map((target) => (
            <button key={target.id} onClick={() => setSelectedId(target.id)} className={`w-full text-left px-4 py-3 border-b border-[hsl(var(--border))] hover:bg-[hsl(var(--surface-2))] ${selected?.id === target.id ? "bg-[hsl(var(--surface-2))]" : ""}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="font-semibold truncate">@{target.twitch_login}</div>
                {target.enabled ? <Eye className="w-4 h-4 text-emerald-300" /> : <EyeOff className="w-4 h-4 text-[hsl(var(--muted-foreground))]" />}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))] mt-1">{target.display_name} - {target.source}</div>
            </button>
          ))}
        </div>

        {selected && (
          <div className="surface p-5 space-y-4">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="label-eyebrow">Selected creator</div>
                <h2 className="text-xl font-semibold mt-1">@{selected.twitch_login}</h2>
              </div>
              <div className="flex flex-wrap gap-2 justify-end">
                <IconButton label="Refresh profile" icon={RefreshCw} onClick={() => targetAction("refresh-profile", "Profile refreshed")} />
                <IconButton label="Regenerate" icon={Sparkles} onClick={() => targetAction("regenerate", "Shoutout regenerated")} />
                <IconButton label="Test" icon={Bot} onClick={() => targetAction("test", "Test shoutout sent")} />
                <IconButton label="Delete" icon={Trash2} danger onClick={deleteTarget} />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Login"><input className={inputClass} value={draft.twitch_login} onChange={(e) => setDraft({ ...draft, twitch_login: e.target.value })} /></Field>
              <Field label="Display name"><input className={inputClass} value={draft.display_name} onChange={(e) => setDraft({ ...draft, display_name: e.target.value })} /></Field>
              <Field label="Source">
                <select className={inputClass} value={draft.source} onChange={(e) => setDraft({ ...draft, source: e.target.value })}>
                  <option value="manual">manual</option>
                  <option value="raid">raid</option>
                </select>
              </Field>
              <label className="flex items-center gap-2 text-sm h-10 mt-5"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> Enabled</label>
            </div>
            <Field label="Notes / manual context"><textarea className={textareaClass} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} /></Field>
            <Field label="Cached shoutout text"><textarea className={textareaClass} value={draft.cached_ai_shoutout} onChange={(e) => setDraft({ ...draft, cached_ai_shoutout: e.target.value })} /></Field>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm text-[hsl(var(--muted-foreground))]">
              <Info label="Last shouted stream" value={selected.last_shouted_stream_id || "Never"} />
              <Info label="Last shouted at" value={selected.last_shouted_at || "Never"} />
              <Info label="Created by" value={selected.created_by || "-"} />
            </div>
            <button onClick={saveTarget} disabled={busy === "save"} className="w-full bg-[hsl(var(--primary))] text-white rounded h-10 flex items-center justify-center gap-2 text-sm"><Save className="w-4 h-4" /> Save Changes</button>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label = "", children } = {}) {
  return <label className="block"><div className="label-eyebrow mb-1.5">{label}</div>{children}</label>;
}

function Info({ label = "", value } = {}) {
  return <div className="rounded border border-[hsl(var(--border))] bg-[hsl(var(--surface-2))] p-3"><div className="label-eyebrow mb-1">{label}</div><div className="text-[hsl(var(--foreground))] break-words">{value}</div></div>;
}

function IconButton({ label = "", icon: Icon, onClick, danger } = {}) {
  return (
    <button onClick={onClick} title={label} className={`border border-[hsl(var(--border))] rounded px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2 ${danger ? "text-[hsl(var(--destructive))]" : ""}`}>
      <Icon className="w-3.5 h-3.5" /> {label}
    </button>
  );
}
